import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Leaf, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GoogleButton } from "@/components/GoogleButton";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data);
      toast.success("Hoş geldiniz!");
      navigate(location.state?.from || "/panel", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Tekrar hoş geldiniz" subtitle="Hesabınıza giriş yapın">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="login-error">{error}</p>}
        <div className="space-y-1.5">
          <Label htmlFor="email">E-posta</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" placeholder="ornek@eposta.com" className="focus-visible:ring-emerald-500" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Şifre</Label>
            <Link to="/sifremi-unuttum" className="text-xs font-medium text-emerald-700 hover:underline">Şifremi unuttum</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" placeholder="••••••••" className="focus-visible:ring-emerald-500" />
        </div>
        <Button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full bg-emerald-600 font-semibold hover:bg-emerald-700">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Giriş Yap
        </Button>
      </form>
      <Divider />
      <GoogleButton label="Google ile giriş yap" />
      <p className="mt-6 text-center text-sm text-slate-600">
        Hesabınız yok mu? <Link to="/kayit" className="font-semibold text-emerald-700 hover:underline">Üye Ol</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-screen bg-slate-50">
      <div className="hidden w-1/2 flex-col justify-between bg-emerald-900 p-12 text-white lg:flex">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-800">
            <Leaf size={20} className="text-emerald-400" />
          </span>
          <span className="font-display text-xl font-extrabold">Çöpcepte</span>
        </Link>
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight">Fazlan çöpe gitmesin,<br />değere dönüşsün.</h2>
          <p className="mt-4 max-w-md text-emerald-100">Stok fazlasından tarlada kalan ürüne, ikinci elden sanayi malzemesine kadar binlerce fırsat.</p>
        </div>
        <p className="text-sm text-emerald-300">Toplu al, değer kat.</p>
      </div>
      <div className="flex w-full items-center justify-center p-6 lg:w-1/2">
        <div className="w-full max-w-md cc-fade-in">
          <Link to="/" className="mb-6 flex items-center gap-2 lg:hidden">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-900"><Leaf size={20} className="text-emerald-400" /></span>
            <span className="font-display text-xl font-extrabold text-emerald-900">Çöpcepte</span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
          <p className="mb-6 mt-1 text-slate-500">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Divider() {
  return (
    <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
      <span className="h-px flex-1 bg-slate-200" />veya<span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}
